import { Component, Inject, OnInit, Optional } from '@angular/core';
import { AppSettingsService } from '@app/core/services/app-settings.service';
import { ILanguageInfo, LanguageService } from '@app/core/services/language.service';
import { ModalService } from '@app/core/services/modal.service';
import { UserStore } from '@app/core/services/user.store';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { IdentityService, IdentityUserDetails } from '@app/services/identity.service';
import { ProductService, ResourceItem } from '@app/services/product.service';
import { SrDeviationRightItem, SrFontSizeItem, SrRefundLevelItem, SrUserGet, SrUserService } from '@app/services/sr-user.service';

@Component({
  selector: 'tt-sr-user',
  templateUrl: './sr-user.component.html',
  styleUrls: ['./sr-user.component.css'],
  standalone: false,
})
export class SrUserComponent implements OnInit {
    public page?: SrUserGet;
    public languages: ILanguageInfo[] = [];
    public fontSizes: SrFontSizeItem[] = [];
    public deviationRights: SrDeviationRightItem[] = [];
    public refundLevels: SrRefundLevelItem[] = [];
    public twoFactorEnabled = false;
    public twoFactorType = '';
    public readonly twoFactorTypes = [{ item_id: 'Email', item_name: 'Email' }, { item_id: 'SmsCode', item_name: 'Phone' }, { item_id: 'SmsAndEmail', item_name: 'Email and Phone' }];
    public saving = false;
    public error = '';
    private currentIdentity?: IdentityUserDetails;
    private editIdentity?: IdentityUserDetails;
    private twoFactorDirty = false;

    constructor(private users: SrUserService, private identity: IdentityService, private language: LanguageService,
        private product: ProductService, private user: UserStore, private settings: AppSettingsService, private modal: ModalService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator) { this.navigator = useNavigatorFor(navigator, this); }

    public get twoFactorDisabled(): boolean {
        return this.settings.settings?.canModifyTwoFaLogin !== true || !this.currentIdentity || !this.editIdentity
            || (this.currentIdentity.userName !== this.editIdentity.userName && this.currentIdentity.isCompanyAdmin !== true);
    }

    public async ngOnInit(): Promise<void> {
        this.user.userEditCache.reset();
        this.currentIdentity = undefined;
        this.editIdentity = undefined;
        this.twoFactorDirty = false;
        const loadPage = async () => {
            this.page = await this.users.getUser(String(this.navigator?.params['portal_user_keyno'] ?? ''));
            try {
                this.editIdentity = await this.identity.getUser(this.page.user_id_portal ?? '');
                this.twoFactorEnabled = this.editIdentity.twoFactorEnabled === true;
                this.twoFactorType = this.editIdentity.twoFactorType ?? '';
            } catch (error) { this.showError(error); }
        };
        const current = async () => { try { this.currentIdentity = await this.identity.getCurrentUser(); } catch (error) { this.showError(error); } };
        try {
            await Promise.all([loadPage(), current(), this.language.List().then(items => this.languages = items),
                this.users.getFontSizes().then(items => this.fontSizes = items), this.users.getDeviationRights().then(items => this.deviationRights = items), this.users.getRefundLevels().then(items => this.refundLevels = items)]);
        } catch (error) { this.showError(error); }
    }

    public searchResources = (params?: { value?: string }) => this.product.searchResources({ resource_searchtext: params?.value ?? this.page?.resource_searchtext ?? '' });
    public selectResource(item?: ResourceItem): void { if (this.page) { this.page.resource_id = item?.item_id ?? ''; this.page.resource_searchtext = item?.item_name ?? ''; } }
    public onResourceTextChanged(value: string): void { if (this.page) { this.page.resource_searchtext = value; if (!value) this.page.resource_id = ''; } }
    public onFontSizeChanged(value: string | number | undefined): void {
        if (!this.page || value === undefined || value === this.page.fontsize) return;
        this.page.fontsize = value;
        if (this.user.userId === this.page.portal_user_keyno) this.user.setFontSize(`${value}px`);
    }
    public onTwoFactorEnabledChanged(value: boolean | string): void {
        if (this.twoFactorDisabled || this.saving) return;
        this.twoFactorEnabled = value === true || value === 'true' || value === '1';
        this.twoFactorDirty = true;
    }
    public onTwoFactorTypeChanged(value: string | undefined): void {
        if (this.twoFactorDisabled || this.saving || value === undefined) return;
        this.twoFactorType = value;
        this.twoFactorDirty = true;
    }

    public async save(): Promise<void> {
        if (!this.page || this.saving) return;
        this.saving = true;
        this.error = '';
        const updateIdentity = this.twoFactorDirty && !this.twoFactorDisabled && !!this.editIdentity;
        try {
            const identitySave = updateIdentity ? (this.twoFactorEnabled ? this.identity.enableTwoFactor(this.editIdentity!.userName, this.twoFactorType) : this.identity.disableTwoFactor(this.editIdentity!.userName)) : Promise.resolve();
            const [userSave, twoFactorSave] = await Promise.allSettled([this.users.saveUser(this.page), identitySave]);
            if (updateIdentity && twoFactorSave.status === 'fulfilled') this.twoFactorDirty = false;
            if (twoFactorSave.status === 'rejected') throw twoFactorSave.reason;
            if (userSave.status === 'rejected') throw userSave.reason;
            if (String(userSave.value.errorcode) !== '0') {
                await this.modal.show({ type: 'warning', title: 'Varsel', message: userSave.value.errormessage,
                    buttons: [{ label: 'OK', cssClass: 'btn-warning', close: true }] });
            } else await this.navigator?.back();
        } catch (error) { this.showError(error); }
        finally { this.saving = false; }
    }
    public sendLoginInfo(): void { if (this.page) this.navigator?.go({ to: 'usersettings_dialogue_userinfo', parms: { portalUserKeyno: this.page.portal_user_keyno } }); }
    private showError(error: unknown): void { this.error = error instanceof Error ? error.message : String(error); }

}
